"use client";

import React from "react";
import { ArrowLeft, ArrowRight, TrendingUp } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const TRENDING_TOPICS = ["Belt & Road Initiative", "EPCO Solutions", "Sustainable Design", "Destination Management", "Cultural Integration", "Investment Trends", "Smart Tourism", "Theme Park Engineering"];
const formatDate = (dateString?: string) => {
  if (!dateString) return "RECENT";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).toUpperCase();
  } catch (e) {
    return "RECENT";
  }
};
function IntelligenceFeed() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  if (loading) {
    return <div className="space-y-12">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="animate-pulse border-b border-border pb-12">
            <div className="h-6 w-32 bg-muted mb-4 rounded"></div>
            <div className="h-8 w-3/4 bg-muted mb-4 rounded"></div>
            <div className="h-4 w-full bg-muted mb-2 rounded"></div>
            <div className="h-4 w-5/6 bg-muted rounded"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center border border-border rounded-xl bg-muted/20">
        <p className="text-destructive mb-4">Failed to load intelligence feed.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>Try Again</Button>
      </div>;
  }
  if (!posts?.length) {
    return <div className="py-12 text-center border border-border rounded-xl bg-muted/20">
        <p className="text-muted-foreground">No articles published yet. Check back soon.</p>
      </div>;
  }
  return <div className="space-y-12">
      {posts.map((post, i) => <article key={post.id} data-index={i} className="group border-b border-border pb-12 last:border-0 last:pb-0">
          <Link to={post.link || "#"} className="block">
            <div className="flex items-start gap-4 md:gap-8">
              <div className="shrink-0 size-20 md:size-32 rounded-lg overflow-hidden bg-muted border border-border/50">
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1581784/img/intelligence_post_thumbnail.png"} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex-1">
                <time className="block font-mono text-xl md:text-2xl text-muted-foreground/70 mb-3 tracking-tight uppercase">
                  {formatDate(post.date)}
                </time>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors duration-300 leading-tight">
                  {post.title}
                </h3>
                {post.excerpt && <div className="text-lg text-muted-foreground leading-relaxed line-clamp-2" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                <div className="mt-6 flex items-center text-sm font-semibold text-primary uppercase tracking-wider group-hover:translate-x-2 transition-transform duration-300">
                  Read Article <ArrowRight className="ml-2 size-4" />
                </div>
              </div>
            </div>
          </Link>
        </article>)}

      {(hasNext || hasPrev) && <div className="flex items-center justify-between pt-8 border-t border-border">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="gap-2">
            <ArrowLeft className="size-4" /> Newer
          </Button>
          <Button variant="outline" onClick={nextPage} disabled={!hasNext} className="gap-2">
            Older <ArrowRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function TourismIntelligence() {
  const wp_query = new WP_Query({
    per_page: 5,
    order: "desc",
    orderby: "date"
  });
  return <section id="tourism-intelligence" className="py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          
          <header className="mb-16 md:mb-24">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Tourism Intelligence
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Professional commentary and analysis on the future of international tourism development.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            
            <div className="lg:col-span-8">
              <PostsProvider wp_query={{
              per_page: 5,
              order: "desc",
              orderby: "date"
            }}>
                <IntelligenceFeed />
              </PostsProvider>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-24 p-8 bg-muted/30 rounded-2xl border border-border/50">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="size-5 text-primary" />
                  <h3 className="text-sm font-bold uppercase tracking-widest text-foreground">
                    Trending Topics
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {TRENDING_TOPICS.map((topic, i) => <Badge key={topic} data-index={i} variant="secondary" className="px-3 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer">
                      {topic}
                    </Badge>)}
                </div>
              </div>
            </aside>

          </div>
        </div>
      </div>
    </section>;
}